import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Product } from "../../Redux/ecommersProduct/types";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import {
  AddToCartRequest,
  CartType,
  ProductType,
} from "../../Redux/custmoer_cart_service/types";

export interface ProductCardProps {
  product: Product;
  badgeText?: string;
  dealBadgeText?: string;
  index?: number;
  className?: string;
  showColumnWrapper?: boolean;
  onWishlistToggle?: (product: Product) => void;
  onSelectOptions?: (product: Product) => void;
  setSelectedProduct?: (product: Product) => void;
  variant?: "default" | "mini";
  detailRoute?: string;
}

const FALLBACK_IMG = "/assets/img/salarlogo.png";
const DEFAULT_BADGES = [
  "New Arrival",
  "Trending",
  "Best Seller",
  "Featured",
  "Limited Edition",
  "Exclusive",
];

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  badgeText,
  dealBadgeText,
  index = 0,
  className = "col-lg-2",
  showColumnWrapper = false,
  onWishlistToggle,
  onSelectOptions,
  setSelectedProduct,
  variant = "default",
  detailRoute = "/DetailPage/:id",
}) => {
  const [isWishlisted, setIsWishlisted] = useState(false);
  const { addItemToCart, loading: cartLoading } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  if (!product) return null;

  const productId = product._id || product.productId || "";
  const detailPath = detailRoute.includes(":id")
    ? detailRoute.replace(":id", productId)
    : `${detailRoute.replace(/\/$/, "")}/${productId}`;
  const name = product.name || "Product";
  const mainImage =
    product.mainImage ||
    product.variations?.[0]?.media?.images?.[0] ||
    FALLBACK_IMG;

  // Pricing details
  const firstVariation = product.variations?.[0];
  const offer = (product as any).featuredOffer;
  const finalPrice =
    offer?.finalPrice ??
    firstVariation?.pricing?.finalPrice ??
    firstVariation?.pricing?.offerPrice ??
    0;
  const mrp =
    offer?.mrp ?? offer?.unitPrice ?? firstVariation?.pricing?.unitPrice ?? 0;

  // Brand Name
  const brandName =
    (product as any).brand?.name ||
    product.vendors?.marketedBy ||
    product.vendors?.manufacturedBy ||
    (typeof product.brandId === "string"
      ? product.brandId
      : product.brandId?.name) ||
    product.sellerData?.businessInfo?.businessName ||
    "Salar Store";

  // Description
  const description = product.description || name;

  // Colors
  const variations = product.variations || [];
  const colors = Array.from(
    new Set(
      variations
        .map(
          (v: any) =>
            v.attributes?.["Color"] ||
            v.attributes?.["color"] ||
            v.attributes?.["Colour"],
        )
        .filter(Boolean),
    ),
  ) as string[];

  // Ratings
  const avgRating = product.reviews?.averageRating ?? 0;
  const totalReviews =
    product.reviews?.totalReviews ?? (product.reviews as any)?.totalCount;
  const formattedTotalReviews =
    totalReviews !== undefined && totalReviews !== null
      ? totalReviews >= 1000
        ? `${(totalReviews / 1000).toFixed(1)}k`
        : `${totalReviews}`
      : "0";

  // Badge text
  const currentBadge =
    badgeText ||
    (product.condition === "new"
      ? "New Arrival"
      : DEFAULT_BADGES[index % DEFAULT_BADGES.length]);

  // Deal badge text
  const currentDealBadge =
    dealBadgeText ||
    (product.deals && product.deals.length > 0
      ? (product.deals[0] as any).dealName ||
        (product.deals[0].dealType
          ? product.deals[0].dealType === "LIGHTNING_DEAL"
            ? "Lightning Deal"
            : "7-Day Deal"
          : "Limited time deal")
      : "Limited time deal");

  // Delivery
  const isFreeDelivery = product.freeDelivery !== false;
  const deliveryTime = product.sameDayDelivery?.status
    ? "Today"
    : product.nextDayDelivery?.status
      ? "Tomorrow"
      : "Tomorrow";

  const handleWishlistClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsWishlisted((prev) => !prev);
    if (onWishlistToggle) {
      onWishlistToggle(product);
    }
  };

  const handleSelectOptionsClick = (e: React.MouseEvent) => {
    if (setSelectedProduct) {
      setSelectedProduct(product);
    }
    if (onSelectOptions) {
      onSelectOptions(product);
    }
  };

  const renderStars = (rating: number) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      let iconClass = "bi bi-star text-black-50 opacity-5";
      if (i <= Math.floor(rating)) {
        iconClass = "bi bi-star-fill text-success";
      } else if (i - rating <= 0.5) {
        iconClass = "bi bi-star-half text-success";
      }
      stars.push(
        <i
          key={i}
          className={`${iconClass} text-xxs ${i > 1 ? "ms-01" : ""}`}
        ></i>,
      );
    }
    return stars;
  };

  const defaultCardContent = (
    <div className="cat-pro-width card border-radius-0 shadow-none mb-lg-0 w-100">
      <Link to={detailPath}>
        <div className="card-header p-0 position-relative border-radius-lg z-index-2 bg-gray-100">
          <span className="badge badge-xs bg-gray-400 border-radius-2xl text-white ms-2">
            {currentBadge}
          </span>
          <div className="d-block blur-shadow-image">
            <img
              src={mainImage}
              alt={name}
              className="img-fluid-cat-pro p-2"
              style={{ height: "16.5rem", objectFit: "contain" }}
              loading="lazy"
              onError={(e) => {
                e.currentTarget.src = FALLBACK_IMG;
              }}
            />
            <div className="offer-text justify-content-end">
              <div
                className="icon bg-white icon-shape border rounded-circle text-center"
                onClick={handleWishlistClick}
                style={{ cursor: "pointer" }}
              >
                <i
                  className={
                    isWishlisted
                      ? "bi bi-heart-fill text-danger text-lg"
                      : "bi bi-heart opacity-5 text-lg"
                  }
                ></i>
              </div>
            </div>
          </div>
        </div>
      </Link>
      <div className="p-2 text-start lh-0">
        <div className="mb-2 mt-1 d-flex justify-content-start align-items-center gap-1">
          {colors.length > 0
            ? colors.slice(0, 4).map((color, cIdx) => (
                <div className="border rounded-circle" key={cIdx}>
                  <div className="border rounded-circle cursor-pointer p-0">
                    <span
                      className={`rounded-circle border avatar ${
                        cIdx === 0 ? "avatar-smm" : "avatar-xs"
                      } p-1`}
                      style={{
                        backgroundColor: color.toLowerCase(),
                        width: 14,
                        height: 14,
                        display: "inline-block",
                      }}
                    ></span>
                  </div>
                </div>
              ))
            : null}
          {/* <div>
            <a href="#" onClick={(e) => e.preventDefault()}>
              <b className="text-xxs text-decoration-underline">
                +{colors.length > 4 ? colors.length - 4 : 3} more
              </b>
            </a>
          </div> */}
        </div>
        <div className="px-2">
          <div className="text-start lh-0">
            <div>
              <b className="font-weight-bold text-sm">
                <Link to={detailPath}>{brandName}</Link>
              </b>
            </div>
            <div className="lh-sm">
              <b className="fw-normal text-xxs text-black">
                <div className="">
                  {description.length < 80
                    ? description
                    : description.slice(0, 80) + "..."}
                </div>
              </b>
            </div>
            <div className="mt-1 mb-2">
              <span>
                <b className="text-black fw-normal text-xxs">
                  {avgRating.toFixed(1)} {renderStars(avgRating)}
                  <b className="text-black-50 fw-normal text-xxs mx-1">
                    ({formattedTotalReviews})
                  </b>
                </b>
              </span>
            </div>
            <div className="mt-1">
              <span className="badge badge-danger badge-sm text-white text-capitalize">
                {currentDealBadge}
              </span>
            </div>
          </div>
          <div className="mb-2 mt-2 px-1">
            <div className="lh-1">
              <b className="font-bold fs-5 text-black my-auto">
                <sup className="fw-normal">₹</sup>
                {finalPrice.toLocaleString("en-IN")}{" "}
              </b>
              {mrp > finalPrice && (
                <span>
                  <b className="fw-normal text-black text-xxs">MRP: </b>{" "}
                  <b className="fw-normal text-black text-xxs text-decoration-line-through">
                    {mrp.toLocaleString("en-IN")}
                  </b>
                </span>
              )}
            </div>
            <div className="lh-base">
              <b className="text-black text-xxs fw-normal">
                <i className="ri-truck-line me-1 text-xs"></i>
                {isFreeDelivery ? "Free Delivery " : "Delivery "}
                <b className="text-black text-xs">{deliveryTime}</b>
              </b>
            </div>
          </div>
        </div>
      </div>
      <div className="text-center">
        <button
          className="btn btn-md w-95 fw-bolder text-black btn-outline-dark"
          onClick={handleSelectOptionsClick}
          data-bs-toggle={onSelectOptions ? undefined : "modal"}
          data-bs-target={onSelectOptions ? undefined : "#proDetailModal"}
        >
          Select Options
          <i className="bi bi-chevron-down ms-1 text-black text-sm"></i>
        </button>
      </div>
    </div>
  );

  const miniCardContent = (
    <div className="card px-1 border-radius-0 shadow-none mb-lg-0">
      <Link to={detailPath}>
        <div className="card-header p-0 position-relative border-radius-00 z-index-2 bg-gray">
          <div className="d-block blur-shadow-image">
            <img
              src={mainImage}
              alt={name}
              className="img-fluid-mini-screens p-1"
              loading="lazy"
              onError={(e) => {
                e.currentTarget.src = FALLBACK_IMG;
              }}
            />
          </div>
        </div>
      </Link>
      {colors.length > 0 && (
        <div className="p-1 text-start lh-0">
          <div className="mb-1 d-flex justify-content-start align-items-center gap-1">
            {colors.slice(0, 4).map((color, cIdx) => (
              <div className="border rounded-circle" key={cIdx}>
                <div className="border rounded-circle cursor-pointer p-0">
                  <span
                    className={`rounded-circle border avatar ${
                      cIdx === 0 ? "avatar-smm" : "avatar-xs"
                    } p-1`}
                    style={{
                      backgroundColor: color.toLowerCase(),
                      width: 14,
                      height: 14,
                      display: "inline-block",
                    }}
                  ></span>
                </div>
              </div>
            ))}
            {colors.length > 4 && (
              <div>
                <a href="#" onClick={(e) => e.preventDefault()}>
                  <b className="text-xxs text-decoration-underline">
                    +{colors.length - 4} more
                  </b>
                </a>
              </div>
            )}
          </div>
        </div>
      )}
      <div className="px-2">
        <div className="text-start lh-0">
          <div>
            <b className="font-weight-bold mt-1 text-sm">
              <Link to={detailPath}>{brandName}</Link>
            </b>
          </div>
          <div className="lh-sm">
            <b className="fw-normal text-xxs text-black">
              <div className="description-truncate-mini">{description}</div>
            </b>
          </div>
          <div className="mt-1 mb-2">
            <span>
              <b className="text-black fw-normal text-xxs">
                {avgRating.toFixed(1)} {renderStars(avgRating)}
                <b className="text-black-50 fw-normal text-xxs mx-1">
                  ({formattedTotalReviews})
                </b>
              </b>
            </span>
          </div>
          <div className="mt-1">
            <span className="badge badge-danger badge-sm text-white text-capitalize">
              {currentDealBadge}
            </span>
          </div>
        </div>
        <div className="mb-2 mt-2 px-1">
          <div className="lh-1">
            <b className="font-bold fs-5 text-black my-auto">
              <sup className="fw-normal">₹</sup>
              {finalPrice.toLocaleString("en-IN")}{" "}
            </b>
            {mrp > finalPrice && (
              <span>
                <b className="fw-normal text-black text-xxs">MRP: </b>{" "}
                <b className="fw-normal text-black text-xxs text-decoration-line-through">
                  {mrp.toLocaleString("en-IN")}
                </b>
              </span>
            )}
          </div>
          <div className="lh-base">
            <b className="text-black text-xxs fw-normal">
              <i className="ri-truck-line me-1 text-xs"></i>
              {isFreeDelivery ? "Free Delivery " : "Delivery "}
              <b className="text-black text-xs">{deliveryTime}</b>
            </b>
          </div>
        </div>
        <div className="pb-2">
          <div className="w-100 mx-auto">
            <button
              className="btn btn-outline-dark btn-sm w-100"
              onClick={handleSelectOptionsClick}
              data-bs-toggle={onSelectOptions ? undefined : "modal"}
              data-bs-target={onSelectOptions ? undefined : "#proDetailModal"}
            >
              Select Options
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const cardContent = variant === "mini" ? miniCardContent : defaultCardContent;

  if (showColumnWrapper) {
    return <div className={className}>{cardContent}</div>;
  }

  return cardContent;
};

export default ProductCard;
